import React, { useContext, useEffect, useState, useCallback } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Container,
  Stack,
} from "@mui/material";
import moment from "moment";
import PropTypes from "prop-types";

import { DataContext } from "../../../context/DataContext";
import { getUserByIdAsync } from "../../../api/Users";
import { getToken } from "../../../api/Account";
import { simpleMessage } from "../../../helpers/Helpers";

const currencyFormatter = new Intl.NumberFormat("es-NI", {
  style: "currency",
  currency: "NIO",
});

const formatDate = (date) => moment(date).format("L");

const TableHeaderCell = ({ children, align = "center" }) => (
  <TableCell
    align={align}
    sx={{
      fontSize: 12,
      fontWeight: "bold",
      color: "#212121",
    }}
  >
    {children}
  </TableCell>
);

const TableDataCell = ({ children, align = "center" }) => (
  <TableCell
    align={align}
    sx={{
      fontSize: 11,
      color: "#212121",
    }}
    component="th"
    scope="row"
  >
    {children}
  </TableCell>
);

export const PrintPayment = React.forwardRef((props, ref) => {
  const { title, slogan, icon, setIsLoading, setIsUnautorized, docTitle } =
    useContext(DataContext);
  const { id, fechaEntrega, creditNumber, montoEntregado, gestor, client } =
    props.credit;

  const [userData, setUserData] = useState(null);

  const token = getToken();

  const getPageMargins = useCallback(() => {
    return `body { color:black; }`;
  }, []);

  const fetchGestorData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getUserByIdAsync(token, gestor);

      if (!result.statusResponse) {
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        throw new Error(result.error);
      }

      setUserData(result.data.result);
    } catch (error) {
      simpleMessage(error.message, "error");
    } finally {
      setIsLoading(false);
    }
  }, [token, gestor, setIsLoading, setIsUnautorized]);

  useEffect(() => {
    fetchGestorData();
  }, [fetchGestorData]);

  if (!userData) return null;

  return (
    <div ref={ref}>
      <style>{getPageMargins()}</style>

      {/* Header Section */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <img src={icon} alt="Company Logo" style={{ width: 70, height: 70 }} />
        <Stack alignItems="center">
          <Typography variant="h5" color="#212121">
            {title}
          </Typography>
          <Typography color="#212121">{slogan}</Typography>
          <Typography variant="h6" color="#212121">
            Plan de Pagos
          </Typography>
        </Stack>
        <img src={icon} alt="Company Logo" style={{ width: 70, height: 70 }} />
      </Stack>

      {/* Client Information */}
      <Stack>
        <Stack direction="row" justifyContent="center" spacing={1}>
          <Typography fontWeight="bold">Cliente:</Typography>
          <Typography>{props.clientName}</Typography>
        </Stack>

        <Typography textAlign="center" fontWeight="bold">
          Municipio de {props.dataCredit.client.community.municipality.name} -
          Barrio: {props.dataCredit.client.community.name}
        </Typography>

        <Stack direction="row" justifyContent="center" spacing={1}>
          <Typography fontWeight="bold">Direccion:</Typography>
          <Typography>{props.address}</Typography>
        </Stack>

        <Stack direction="row" justifyContent="center" spacing={1}>
          <Typography fontWeight="bold">
            Negocio o Centro de trabajo:
          </Typography>
          <Typography>{client.company}</Typography>
        </Stack>
      </Stack>

      {/* Credit Information */}
      <Stack
        direction="row"
        justifyContent="center"
        spacing={3}
        flexWrap="wrap"
      >
        <Stack direction="row" spacing={1}>
          <Typography fontWeight="bold">Telefono Cliente:</Typography>
          <Typography>{client.phone}</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Typography fontWeight="bold">Credito #:</Typography>
          <Typography>{creditNumber}</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Typography fontWeight="bold">Monto entregado:</Typography>
          <Typography>{currencyFormatter.format(montoEntregado)}</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Typography fontWeight="bold">Fecha Desembolso:</Typography>
          <Typography>{formatDate(fechaEntrega)}</Typography>
        </Stack>
      </Stack>

      {/* Manager Information */}
      <Stack
        direction="row"
        justifyContent="center"
        spacing={3}
        flexWrap="wrap"
      >
        <Stack direction="row" spacing={1}>
          <Typography fontWeight="bold">Gestor:</Typography>
          <Typography>{userData.fullName}</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Typography fontWeight="bold">Telefono:</Typography>
          <Typography>{userData.phone}</Typography>
        </Stack>
      </Stack>

      <Typography
        fontSize={13}
        color="#f50057"
        fontWeight={"bold"}
        textAlign="center"
        mt={2}
      >
        {docTitle}, no realiza ninguna deduccion en los desembolsos, en caso de
        sufrir una deduccion, por favor informar al 7664 0133
      </Typography>

      {/* Payment Table */}
      <Container maxWidth="lg">
        <TableContainer>
          <Table size="small" sx={{ border: 1, borderColor: "#212121" }}>
            <TableHead>
              <TableRow>
                <TableHeaderCell align="left">#</TableHeaderCell>
                <TableHeaderCell>Fecha Pago</TableHeaderCell>
                <TableHeaderCell>Saldo Anterior</TableHeaderCell>
                <TableHeaderCell>Cuota Pagar</TableHeaderCell>
                <TableHeaderCell>Nuevo Saldo</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {props.paymentList.map((row) => (
                <TableRow key={row.id}>
                  <TableDataCell align="left">{row.numeroCuota}</TableDataCell>
                  <TableDataCell>{formatDate(row.fechaPago)}</TableDataCell>
                  <TableDataCell>
                    {currencyFormatter.format(row.saldoAnterior)}
                  </TableDataCell>
                  <TableDataCell>
                    {currencyFormatter.format(row.cuotaPrincipal)}
                  </TableDataCell>
                  <TableDataCell>
                    {currencyFormatter.format(row.nuevoSaldo)}
                  </TableDataCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography fontSize={13} color="#f50057" textAlign="justify" mt={2}>
          Apreciado cliente de{" "}
          <span style={{ fontWeight: "bold" }}>{docTitle}</span>, por su
          seguridad y control financiero, recuerde que es su derecho y deber
          comprobar su saldo en su comprobante, debe exigir su recibo impreso o
          digital inmediatamente después de realizar cualquier abono al gestor
          de cobro, en caso de no recibir su comprobante por favor contactese al
          76640133
        </Typography>

        {/* Signature Section */}
        <Stack direction="row-reverse" spacing={1} mt={10} alignItems="center">
          <Typography sx={{ textDecoration: "overline" }}>
            {props.clientName}
          </Typography>
          <Typography>FIRMA DEUDOR:</Typography>
        </Stack>
      </Container>
    </div>
  );
});

PrintPayment.propTypes = {
  credit: PropTypes.shape({
    id: PropTypes.any.isRequired,
    fechaEntrega: PropTypes.string.isRequired,
    creditNumber: PropTypes.string.isRequired,
    montoEntregado: PropTypes.number.isRequired,
    gestor: PropTypes.string.isRequired,
  }).isRequired,
  clientName: PropTypes.string.isRequired,
  address: PropTypes.string.isRequired,
  paymentList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.any.isRequired,
      numeroCuota: PropTypes.number.isRequired,
      fechaPago: PropTypes.string.isRequired,
      saldoAnterior: PropTypes.number.isRequired,
      cuotaPrincipal: PropTypes.number.isRequired,
      nuevoSaldo: PropTypes.number.isRequired,
    }),
  ).isRequired,
};

export default PrintPayment;
